import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { ActionType as TableActionType } from 'react-table';

import { PAGE_SIZE } from '~/common/constants/page';
import { EntityVisibilityType } from '~/components/Shared/EntityVisibilitySelect/types';
import { PersistedTable } from '~/components/Shared/PersistedTable/PersistedTable';
import {
  FilterInstance,
  TableToggleFilter,
} from '~/components/Shared/TableToggleFilter/TableToggleFilter';
import { User } from '~/components/Shared/User/User';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { useModal } from '~/eds/src/components/hooks/useModal';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Markdown } from '~/eds/src/components/Markdown/Markdown';
import { PageLayout } from '~/eds/src/modules/PageLayout/PageLayout';
import { Nullable } from '~/eds/src/types/Nullable';
import { TableContextType } from '~/enums/TableContextType';
import { TableViewContextType } from '~/enums/TableViewType';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { useTableSettings } from '~/hooks/useTableSettings';
import { api } from '~/redux/api/createApi';
import { useRouting } from '~/routing/useRouting';
import { ColumnSortOrder } from '~/types/Columns';
import { testIsAdmin } from '~/utils/user';

import { SaveEditEntityModal } from '../../SaveEditEntityModal/SaveEditEntityModal';
import { EntityVisibilityQueryParam, SavedSearch } from '../../types/search';
import { convertQueryV2ToQueryV3 } from '../../utils/convertQueryV2ToQueryV3';
import { testIsQueryV2, testIsQueryV3 } from '../../utils/queryUtils';

export const SavedSearchesList = () => {
  const currentUser = useCurrentUser();
  const intl = useIntl();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortBy] = useState<ColumnSortOrder | undefined>();
  const [typesParam, setTypesParam] = useState<EntityVisibilityQueryParam[]>(
    [],
  );
  const [activeItem, setActiveItem] = useState<Nullable<SavedSearch>>(null);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const { toast } = useToast();
  const { navigate } = useRouting();
  //RTKQ
  const { data, isLoading, isFetching, isError, refetch } =
    api.endpoints.getSearchV3SavedSearches.useQuery({
      page,
      pageSize,
      sort: sortBy,
      types: typesParam,
    });
  const [deleteSavedSearch] =
    api.endpoints.deleteSearchV3SavedSearch.useMutation();
  const [deleteV2SavedSearch] = api.endpoints.deleteSavedSearch.useMutation();
  const [saveSavedSearch] = api.endpoints.postSavedSearch.useMutation();
  const [putSavedSearch] = api.endpoints.putSavedSearch.useMutation();

  const { data: currentTableViewSet, isLoading: isLoadingCurrentTableViewSet } =
    api.endpoints.getDefaultTableView.useQuery({
      context: TableViewContextType.SEARCH,
    });

  const resultsTable: string[] = useMemo(() => {
    return currentTableViewSet?.map((column) => `${column.field_id}`) ?? [];
  }, [currentTableViewSet]);

  useEffect(() => {
    if (isError) {
      toast({
        status: 'danger',
        message: intl.formatMessage({
          id: 'SavedSearchesList.fetchErrorMessage',
          defaultMessage: 'An error occured while fetching saved searches.',
          description: 'Error message when fetching saved searches fails',
        }),
      });
    }
  }, [isError]);

  const [deleteModal, showDeleteModal, hideDeleteModal] = useModal({
    title: intl.formatMessage({
      id: 'SavedSearchesList.deleteModalTitle',
      defaultMessage: 'Delete Saved Search',
      description: 'Title for delete saved search confirmation modal',
    }),
    children: (
      <Markdown
        text={intl.formatMessage(
          {
            id: 'SavedSearchesList.deleteConfirmationMessage',
            defaultMessage:
              'Are you sure you want to delete ***{searchName}*** saved search?',
            description:
              'Confirmation message for deleting a saved search. Example: searchName = "My Contract Search"',
          },
          { searchName: activeItem?.name },
        )}
      />
    ),
    primaryAction: {
      onClick: async () => {
        try {
          if (activeItem) {
            if (activeItem.version === 3) {
              await deleteSavedSearch({ id: activeItem.id }).unwrap();
            } else {
              await deleteV2SavedSearch(activeItem.id as number).unwrap();
            }
            toast({
              status: 'success',
              message: intl.formatMessage({
                id: 'SavedSearchesList.deleteSuccessMessage',
                defaultMessage: 'Saved Search successfully deleted.',
                description: 'Success message when saved search is deleted',
              }),
            });
          }
        } catch {
          toast({
            status: 'danger',
            message: intl.formatMessage({
              id: 'SavedSearchesList.deleteErrorMessage',
              defaultMessage:
                'An error occured while deleting the saved search',
              description: 'Error message when deleting saved search fails',
            }),
          });
        } finally {
          hideDeleteModal();
        }
      },
      variant: 'danger',
      text: intl.formatMessage({
        id: 'SavedSearchesList.deleteButtonText',
        defaultMessage: 'DELETE',
        description: 'Text for delete button in confirmation modal',
      }),
    },
  });

  const loadingContent = {
    isLoading: isLoading || isLoadingCurrentTableViewSet,
  };

  const placeholderContent = isError
    ? {
        title: intl.formatMessage({
          id: 'SavedSearchesList.fetchErrorTitle',
          defaultMessage: 'An error occured while fetching saved searches.',
          description:
            'Title for error state when fetching saved searches fails',
        }),
        image: 'error-page-error',
      }
    : data?.results.length === 0
      ? {
          title: intl.formatMessage({
            id: 'SavedSearchesList.noSearchesFoundTitle',
            defaultMessage: 'No saved searches found',
            description: 'Title when no saved searches are found',
          }),
        }
      : undefined;

  const {
    tableSettings = {
      pageSize: PAGE_SIZE,
      columnWidths: {},
    },
  } = useTableSettings(TableContextType.SavedSearches);

  const handleClickSearchV2 = async (savedSearch: SavedSearch) => {
    if (testIsQueryV2(savedSearch)) {
      try {
        const response = await saveSavedSearch({
          name: savedSearch.name,
          query: convertQueryV2ToQueryV3(savedSearch.query),
          filters: savedSearch.filters ?? [],
          visibility: savedSearch.visibility,
          userIds: savedSearch.userIds,
          departmentIds: savedSearch.departmentIds,
          resultTable: resultsTable.map((column, index) => ({
            filterId: column,
            columnIndex: index,
          })),
          searchV2Id: savedSearch.id as number,
        }).unwrap();
        if (response) {
          navigate(`/search/${response.id}`);
        }
      } catch {
        toast({
          status: 'danger',
          message: intl.formatMessage({
            id: 'SavedSearchesList.openSearchErrorMessage',
            defaultMessage: 'An error occured while opening saved search.',
            description: 'Error message when opening saved search fails',
          }),
        });
      }
    }
  };

  const columns = [
    {
      key: 'name',
      cellType: 'link',
      title: intl.formatMessage({
        id: 'SavedSearchesList.nameColumnTitle',
        defaultMessage: 'Name',
        description: 'Title for name column in saved searches table',
      }),
      width: 'l',
      mapCellProps: (savedSearch: SavedSearch) => ({
        text: savedSearch.name,
        pathname: `/search/${savedSearch.id}`,
        onClick: () => {
          if (testIsQueryV3(savedSearch)) {
            navigate(`/search/${savedSearch.id}`);
          }
          handleClickSearchV2(savedSearch);
        },
      }),
    },
    {
      key: 'created_by',
      cellType: 'user',
      title: intl.formatMessage({
        id: 'SavedSearchesList.createdByColumnTitle',
        defaultMessage: 'Created By',
        description: 'Title for created by column in saved searches table',
      }),
      disableSortBy: true,
      mapCellProps: ({ userId }: SavedSearch) => ({
        asyncUser: {
          id: userId,
          render: User,
        },
        mode: 'avatar-name',
      }),
    },
    {
      key: 'created_date',
      cellType: 'datetime',
      title: intl.formatMessage({
        id: 'SavedSearchesList.createdDateColumnTitle',
        defaultMessage: 'Created on date',
        description: 'Title for created date column in saved searches table',
      }),
      width: 'l',
      disableSortBy: true,
      mapCellProps: ({ dateAdded }: SavedSearch) => ({
        datetime: dateAdded,
      }),
    },
  ];

  const handleEditSavedSearch = async (
    data: EntityVisibilityType & { name: string },
  ) => {
    if (activeItem) {
      try {
        let response: SavedSearch | undefined = undefined;
        if (testIsQueryV2(activeItem)) {
          response = await saveSavedSearch({
            name: data.name,
            query: convertQueryV2ToQueryV3(activeItem.query),
            filters: activeItem.filters ?? [],
            visibility: data.visibility,
            userIds: data.userIds,
            departmentIds: data.departmentIds,
            resultTable: resultsTable.map((column, index) => ({
              filterId: column,
              columnIndex: index,
            })),
            searchV2Id: activeItem.id as number,
          }).unwrap();
        } else if (testIsQueryV3(activeItem)) {
          response = await putSavedSearch({
            id: activeItem.id,
            savedSearch: {
              name: data.name,
              visibility: data.visibility,
              userIds: data.userIds,
              departmentIds: data.departmentIds,
              query: activeItem.query,
              filters: activeItem.filters,
              resultTable: resultsTable.map((column, index) => ({
                filterId: column,
                columnIndex: index,
              })),
            },
          }).unwrap();
        }
        toast({
          status: 'success',
          message: intl.formatMessage(
            {
              id: 'SavedSearchesList.editSuccessMessage',
              defaultMessage:
                'Saved Search **{searchName}** successfully edited.',
              description:
                'Success message when saved search is edited. Example: searchName = "My Contract Search"',
            },
            { searchName: response?.name ?? '' },
          ),
        });
        setIsEditModalVisible(false);
        refetch();
      } catch {
        toast({
          status: 'danger',
          message: intl.formatMessage({
            id: 'SavedSearchesList.editErrorMessage',
            defaultMessage: 'An error occured while editing saved search.',
            description: 'Error message when editing saved search fails',
          }),
        });
      }
    }
  };

  const canEditSavedSearch = (search: SavedSearch) =>
    testIsQueryV2(search)
      ? search.userId === currentUser.id
      : search.userId === currentUser.id || testIsAdmin(currentUser);

  const rowActions = [
    {
      condition: (row: SavedSearch) =>
        row.userId === currentUser.id || testIsAdmin(currentUser),
      label: intl.formatMessage({
        id: 'SavedSearchesList.deleteActionLabel',
        defaultMessage: 'Delete',
        description: 'Label for delete row action',
      }),
      onClick: (row: SavedSearch) => {
        setActiveItem(row);
        showDeleteModal();
      },
    },
    {
      condition: canEditSavedSearch,
      label: intl.formatMessage({
        id: 'SavedSearchesList.editActionLabel',
        defaultMessage: 'Edit',
        description: 'Label for edit row action',
      }),
      onClick: (row: SavedSearch) => {
        setActiveItem(row);
        setIsEditModalVisible(true);
      },
    },
  ];

  const handlePaginate = ({ pageIndex }: { pageIndex: number }) => {
    if (pageIndex !== page) {
      setPage(pageIndex);
    }
  };

  const onPageSizeChange = (pageSize: number) => {
    setPage(1);
    setPageSize(pageSize);
  };

  const handleUpdate = (state: any, action?: TableActionType) => {
    if (action?.type === 'toggleSortBy') {
      setSortBy(state.sortBy[0]);
    }
  };

  // Update filterOptions at the top of the component
  const localizedFilterOptions: FilterInstance<EntityVisibilityQueryParam>[] = [
    {
      text: intl.formatMessage({
        id: 'SavedSearchesList.mySearchesFilter',
        defaultMessage: 'My Saved Searches',
        description: "Filter option for user's own saved searches",
      }),
      value: 'mine',
    },
    {
      text: intl.formatMessage({
        id: 'SavedSearchesList.sharedWithMeFilter',
        defaultMessage: 'Shared with me',
        description: 'Filter option for searches shared with the user',
      }),
      value: 'shared',
    },
  ];

  return (
    <>
      <PageLayout
        loadingContent={loadingContent}
        title={intl.formatMessage({
          id: 'SavedSearchesList.pageTitle',
          defaultMessage: 'Saved Searches',
          description: 'Page title for saved searches list',
        })}
      >
        <TableToggleFilter
          options={localizedFilterOptions}
          selectedOptions={typesParam}
          onChange={setTypesParam}
        />
        <ContentContainer placeholderContent={placeholderContent}>
          <PersistedTable
            name="saved searches"
            context={TableContextType.SavedSearches}
            totalCount={data?.meta.total}
            columns={columns}
            data={data?.results}
            rowActions={rowActions}
            isLoading={isFetching}
            state={{
              pageIndex: page,
              pageSize,
              columnWidths: tableSettings.columnWidths,
              sortBy: [sortBy],
            }}
            onUpdate={handleUpdate}
            onPaginate={handlePaginate}
            onPageSizeChange={onPageSizeChange}
            options={{
              enableExportXlsx: false,
              enableSelectRows: false,
              enablePageSizeSelect: true,
            }}
          />
        </ContentContainer>
      </PageLayout>
      {deleteModal}
      {isEditModalVisible && (
        <SaveEditEntityModal
          placeholder={intl.formatMessage({
            id: 'SavedSearchesList.searchNamePlaceholder',
            defaultMessage: 'Saved Search Name',
            description: 'Placeholder text for saved search name input',
          })}
          initialValues={
            activeItem
              ? {
                  name: activeItem.name,
                  visibility: activeItem.visibility,
                  userIds: activeItem.userIds,
                  departmentIds: activeItem.departmentIds,
                }
              : undefined
          }
          onSubmit={handleEditSavedSearch}
          onHide={() => setIsEditModalVisible(false)}
          isVisible={isEditModalVisible}
          title={intl.formatMessage({
            id: 'SavedSearchesList.editSearchTitle',
            defaultMessage: 'Edit Search',
            description: 'Title for edit search modal',
          })}
        />
      )}
    </>
  );
};
